<script setup>
import {navigate} from '@ohrm/core/util/helper/navigation';
import {
  required,
  shouldNotExceedCharLength,
} from '@ohrm/core/util/validation/rules';
import {APIService} from '@ohrm/core/util/services/api.service';
import ReportDisplayField from '@/orangehrmPimPlugin/components/ReportDisplayField';
import ReportCriterion from '@/orangehrmPimPlugin/components/ReportCriterion';
import {inject, ref, defineProps, onBeforeMount, computed} from 'vue';
import usei18n from '@/core/util/composable/usei18n';

const props = defineProps({
  reportId: {
    type: Number,
    required: false,
    default: () => null,
  },
  reportType: {
    type: String,
    required: true,
  },
  selectionCriteria: {
    type: Array,
    required: true,
    default: () => [],
  },
  displayFieldGroups: {
    type: Array,
    required: true,
    default: () => [],
  },
  displayFields: {
    type: Array,
    required: true,
    default: () => [],
  },
});

const $toast = inject('$toast');

const http = new APIService(
  window.appGlobal.baseUrl,
  `/api/v2/training/save-report`,
);

const report = ref({
  name: '',
  criterion: null,
  criteriaSelected: [],
  criteriaFieldValues: {},
  fieldGroup: null,
  fieldGroupSelected: [],
  displayField: null,
  displayFieldSelected: {},
  type: props.reportType,
});
const isLoading = ref(false);
const rules = ref({
  name: [required, shouldNotExceedCharLength(250)],
  includeEmployees: [required],
});

const getAllDisplayFieldsByGroupId = groupId => {
  const fieldGroup = props.displayFields.find(
    group => group.field_group_id === groupId,
  );
  return fieldGroup ? fieldGroup.fields : [];
};

const getUnusedDisplayFieldsByGroupId = groupId => {
  const selectedFieldGroup = report.value.displayFieldSelected[groupId];
  const usedDisplayFields = selectedFieldGroup ? selectedFieldGroup.fields : [];
  return getAllDisplayFieldsByGroupId(groupId).filter(
    field => !usedDisplayFields.find(f => f.id === field.id),
  );
};

const availableFieldGroups = computed(() => {
  return props.displayFieldGroups.filter(
    group => getUnusedDisplayFieldsByGroupId(group.id).length !== 0,
  );
});

const availableDisplayFields = computed(() => {
  const fieldGroupId = report.value.fieldGroup?.id;
  return fieldGroupId ? getUnusedDisplayFieldsByGroupId(fieldGroupId) : [];
});

const availableCriteria = computed(() => {
  return props.selectionCriteria.filter(
    criterion =>
      !report.value.criteriaSelected.find(c => c.id === criterion.id),
  );
});

onBeforeMount(() => {
  if (props.reportId) {
    http
      .request({
        method: 'GET',
        url: `/api/v2/pim/reports/defined/${props.reportId}`,
      })
      .then(response => {
        const {data} = response.data;
        report.value.name = data.name;
        const operators = [
          {id: 'eq', label: 'Equal'},
          {id: 'lt', label: 'Less Than'},
          {id: 'gt', label: 'Greater Than'},
          {id: 'between', label: 'Range'},
        ];
        for (const key in data.fieldGroup) {
          const fieldGroup = props.displayFields.find(
            group => group.field_group_id == key,
          );
          report.value.fieldGroupSelected.push(
            props.displayFieldGroups.find(group => group.id == key),
          );
          report.value.displayFieldSelected[key] = {
            fields: data.fieldGroup[key].fields.map(id =>
              fieldGroup.fields.find(field => field.id === id),
            ),
            includeHeader: data.fieldGroup[key].includeHeader,
          };
        }
        for (const key in data.criteria) {
          const criterion = props.selectionCriteria.find(
            criterion => criterion.id == key,
          );
          report.value.criteriaSelected.push(criterion);
          report.value.criteriaFieldValues[key] = {
            valueX: data.criteria[key].x,
            valueY:
              data.criteria[key].y === 'undefined'
                ? null
                : data.criteria[key].y,
            operator: operators.find(o => o.id === data.criteria[key].operator),
          };
        }
      })
      .finally(() => {
        isLoading.value = false;
      });
  } else if (props.reportType == 'matrix') {
    const key = 23;
    const criterion = props.selectionCriteria.find(
      criterion => criterion.id == key,
    );
    report.value.criteriaSelected.push(criterion);
    report.value.criteriaFieldValues[key] = {
      valueX: null,
      valueY: null,
      operator: null,
    };
  }
});

const addCriterion = () => {
  const criterion = report.value.criterion;
  if (criterion) {
    report.value.criteriaSelected.push(criterion);
    report.value.criteriaFieldValues[criterion.id] = {
      valueX: null,
      valueY: null,
      operator: null,
    };
    report.value.criterion = null;
  }
};

const removeCriterion = index => {
  const criterion = report.value.criteriaSelected.splice(index, 1);
  delete report.value.criteriaFieldValues[criterion[0].id];
};

const addDisplayField = () => {
  const fieldGroup = report.value.fieldGroup;
  const displayField = report.value.displayField;
  if (fieldGroup) {
    const groupIndex = report.value.fieldGroupSelected.findIndex(
      group => group.id === fieldGroup.id,
    );
    if (groupIndex === -1) {
      report.value.fieldGroupSelected.push(fieldGroup);
      report.value.displayFieldSelected[fieldGroup.id] = {
        fields: [],
        includeHeader: false,
      };
    }
    if (displayField) {
      report.value.displayFieldSelected[fieldGroup.id].fields.push(
        displayField,
      );
      report.value.displayField = null;
    } else {
      getUnusedDisplayFieldsByGroupId(fieldGroup.id).forEach(displayField => {
        report.value.displayFieldSelected[fieldGroup.id].fields.push(
          displayField,
        );
      });
    }
    // unselect fieldGroup if all fields are used
    if (getUnusedDisplayFieldsByGroupId(fieldGroup.id).length === 0) {
      report.value.fieldGroup = null;
    }
  }
};

const removeDisplayFieldGroup = index => {
  const fieldGroup = report.value.fieldGroupSelected[index];
  report.value.fieldGroupSelected.splice(index, 1);
  delete report.value.displayFieldSelected[fieldGroup.id];
};

const removeDisplayField = (item, index) => {
  const fieldGroup = report.value.fieldGroupSelected[index];
  const fields = report.value.displayFieldSelected[fieldGroup.id].fields;
  report.value.displayFieldSelected[fieldGroup.id].fields = fields.filter(
    field => field.id !== item.id,
  );
  // remove field group if no fields
  if (report.value.displayFieldSelected[fieldGroup.id].fields.length === 0) {
    removeDisplayFieldGroup(index);
  }
};

const serializeBody = reportModel => {
  const payload = {
    name: reportModel.name,
    type: reportModel.type,
    criteria: {},
    fieldGroup: {},
  };
  reportModel.fieldGroupSelected.forEach(group => {
    const fields = reportModel.displayFieldSelected[group.id].fields;
    const includeHeader =
      reportModel.displayFieldSelected[group.id].includeHeader;
    payload.fieldGroup[group.id] = {
      fields: fields.map(field => field.id),
      includeHeader,
    };
  });
  reportModel.criteriaSelected.forEach(criterion => {
    const criteriaField = reportModel.criteriaFieldValues[criterion.id];
    payload.criteria[criterion.id] = {
      operator: criteriaField.operator ? criteriaField.operator.id : '',
      x: criteriaField.valueX,
      y:
        typeof criteriaField.valueY === 'object'
          ? String(criteriaField.valueY?.id)
          : criteriaField.valueY,
    };
  });

  return payload;
};

const capitalizeFirstLetter = val => {
  return (
    String(val)
      .charAt(0)
      .toUpperCase() + String(val).slice(1)
  );
};

const onCancel = () => {
  navigate(
    `/training/viewTraining${capitalizeFirstLetter(props.reportType)}Report`,
  );
};

const onSave = () => {
  if (Object.keys(report.value.displayFieldSelected).length === 0) {
    return $toast.warn({
      title: usei18n().$t('general.warning'),
      message: usei18n().$t('pim.at_least_one_display_field_should_be_added'),
    });
  }

  isLoading.value = true;
  let reportId = null;
  let data = {report: serializeBody(report.value)};
  if (props.reportId) {
    data = {
      ...data,
      reportId: props.reportId,
    };
  }

  http
    .request({
      method: 'POST',
      data: {
        ...data,
      },
    })
    .then(response => {
      reportId = response.data.id;
      return $toast.saveSuccess();
    })
    .then(() => {
      reportId &&
        navigate(`/training/${props.reportType}/viewTrainingReport/{id}`, {
          id: reportId,
        });
    })
    .finally(() => {
      isLoading.value = false;
    });
};
</script>

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">{{
        $t('pim.add_report')
      }}</oxd-text>
      <oxd-divider />

      <oxd-form :loading="isLoading" @submitValid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="report.name"
                :label="$t('general.report_name')"
                :placeholder="$t('general.type_here_message')"
                :rules="rules.name"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-row>
          <oxd-text class="orangehrm-sub-title" tag="h6">
            {{ $t('pim.selection_criteria') }}
          </oxd-text>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item class="orangehrm-report-criteria --span-column-2">
              <oxd-input-field
                v-model="report.criterion"
                type="select"
                :label="$t('pim.selection_criteria')"
                :options="availableCriteria"
              />
              <oxd-input-group>
                <oxd-icon-button
                  class="orangehrm-report-icon"
                  name="plus"
                  @click="addCriterion"
                />
              </oxd-input-group>
            </oxd-grid-item>
            <report-criterion
              v-for="(criterion, index) in report.criteriaSelected"
              :key="criterion"
              v-model:operator="
                report.criteriaFieldValues[criterion.id].operator
              "
              v-model:valueX="report.criteriaFieldValues[criterion.id].valueX"
              v-model:valueY="report.criteriaFieldValues[criterion.id].valueY"
              :criterion="criterion"
              :hide-delete-btn="criterion.id == 23 && reportType == 'matrix'"
              @delete="removeCriterion(index)"
            >
            </report-criterion>
            <!-- end selected criteria fields -->
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-row>
          <oxd-text class="orangehrm-sub-title" tag="h6">
            {{ $t('pim.display_fields') }}
          </oxd-text>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="report.fieldGroup"
                type="select"
                :label="$t('pim.select_display_field_group')"
                :options="availableFieldGroups"
              />
            </oxd-grid-item>
            <oxd-grid-item class="orangehrm-report-criteria --span-column-2">
              <oxd-input-field
                v-model="report.displayField"
                type="select"
                :label="$t('pim.select_display_field')"
                :options="availableDisplayFields"
              />
              <oxd-input-group>
                <oxd-icon-button
                  class="orangehrm-report-icon"
                  name="plus"
                  @click="addDisplayField"
                />
              </oxd-input-group>
            </oxd-grid-item>

            <!-- start display group fields -->
            <report-display-field
              v-for="(fieldGroup, index) in report.fieldGroupSelected"
              :key="fieldGroup"
              v-model:includeHeader="
                report.displayFieldSelected[fieldGroup.id].includeHeader
              "
              :field-group="fieldGroup"
              :selected-fields="
                report.displayFieldSelected[fieldGroup.id].fields
              "
              @delete="removeDisplayFieldGroup(index)"
              @deleteChip="removeDisplayField($event, index)"
            >
            </report-display-field>
            <!-- end display group fields -->
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <oxd-button
            type="button"
            display-type="ghost"
            :label="$t('general.cancel')"
            @click="onCancel"
          />
          <submit-button />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.orangehrm-report {
  &-criteria {
    display: flex;
    align-items: center;
  }
  &-icon {
    margin-left: 1rem;
  }
}

::v-deep(.oxd-chip .oxd-icon) {
  align-self: center;
}
</style>
